// Grouping for the calendar's stacked competing holds (feat/ui-holds).
//
// When a this-week slot carries MULTIPLE overbookable ghost holds (the model
// double-holds a near slot and whoever pays first locks it, voiding the rest),
// the calendar shows them as ONE tile at normal booking width with the holder
// list shown by default, rather than splitting the column into skinny lanes.
//
// This module is the pure grouping step: it clusters the overbookable held
// ghosts on a single artist/day that overlap in time. It mirrors the model's
// own overlap rule in `recallOverlappingHolds` (same artist, half-open interval
// overlap) so what the calendar groups is exactly the set the model competes.
import type { Booking } from '@/domain/types';

/** A ghost hold that may be double-held: a held booking flagged overbookable. */
export function isOverbookableHold(booking: Booking): boolean {
  return booking.status === 'held' && booking.overbookable === true;
}

export interface HoldCluster {
  /** Stable id for the cluster: the earliest-starting hold's id. */
  id: string;
  /** The competing holds, in start order (display re-sorts by lapse time). */
  holds: Booking[];
  /** Earliest start across the cluster, ms since epoch. */
  startMs: number;
  /** Latest end across the cluster, ms since epoch. */
  endMs: number;
}

export interface GroupedHolds {
  /** Clusters of 2+ overlapping overbookable holds — rendered as a stacked tile. */
  clusters: HoldCluster[];
  /** Lone overbookable holds (a cluster of one) — rendered as a normal tile. */
  loneHolds: Booking[];
}

/**
 * Cluster the overbookable held ghosts on ONE artist/day that overlap in time.
 * `bookings` is assumed already scoped to a single artist on a single day.
 * Only clusters of 2+ become stacked cells; a lone hold falls back to normal
 * lane-packing so it reads exactly as it does today.
 */
export function groupOverbookableHolds(bookings: Booking[]): GroupedHolds {
  const holds = bookings
    .filter(isOverbookableHold)
    .slice()
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  const clusters: HoldCluster[] = [];
  const loneHolds: Booking[] = [];

  let current: Booking[] = [];
  let currentEnd = Number.NEGATIVE_INFINITY;

  const flush = () => {
    if (current.length >= 2) {
      const startMs = Math.min(...current.map((b) => new Date(b.start).getTime()));
      const endMs = Math.max(
        ...current.map((b) => new Date(b.start).getTime() + b.durationMin * 60_000),
      );
      clusters.push({ id: current[0]!.id, holds: current, startMs, endMs });
    } else if (current.length === 1) {
      loneHolds.push(current[0]!);
    }
    current = [];
    currentEnd = Number.NEGATIVE_INFINITY;
  };

  for (const booking of holds) {
    const startMs = new Date(booking.start).getTime();
    const endMs = startMs + booking.durationMin * 60_000;
    if (current.length === 0 || startMs < currentEnd) {
      // Overlaps the running cluster (half-open interval): keep stacking.
      current.push(booking);
      currentEnd = Math.max(currentEnd, endMs);
    } else {
      flush();
      current.push(booking);
      currentEnd = endMs;
    }
  }
  flush();

  return { clusters, loneHolds };
}
